import ChevronRight from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import UnfoldMore from "@mui/icons-material/UnfoldMore";
import {
  Box,
  Breadcrumbs,
  Button,
  InputAdornment,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router";
import { usePopover } from "../../../utils/hooks/use-popover";
import { useProgramOverview } from "../../program-landing/hooks/use-program-overview";
import { ProgramIcon } from "../../programs/components/program-icon";
import { flattenProgramTree, programPath } from "../program-tree";

import type { ProgramNode, ProgramTree } from "../program-tree";
import type { FC, ReactNode } from "react";

export const programHref = (id: string): string => `/tabbed-programs/${id}`;

/** The popover of a switcher, below its button. Its content unmounts when it closes. */
export const ProgramTreePopover: FC<{
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  label: string;
  children: ReactNode;
}> = ({ anchorEl, open, onClose, label, children }) => (
  <Popover
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    slotProps={{ paper: { role: "dialog", "aria-label": label, sx: { width: 360 } } }}
  >
    {children}
  </Popover>
);

/**
 * A search over the names of the programs, and the tree of programs, for a switcher. A row opens its
 * program, and a program the caller cannot open is grey. The current program is selected. With no
 * current program, the programs at the top are bold. A star marks each favourite. Each program that
 * the caller can open shows its number of Communications, with its folders, once they load. The
 * numbers load each time the switcher opens.
 */
export const ProgramTreeList: FC<{
  tree: ProgramTree;
  label: string;
  empty: string;
  current?: ProgramNode;
  favourites?: readonly string[];
  onPick: () => void;
}> = ({ tree, label, empty, current, favourites, onPick }) => {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { data: overviews } = useProgramOverview();
  const counts = overviews
    ? new Map(overviews.map(({ id, communications }) => [id, communications]))
    : undefined;

  const search = query.trim().toLowerCase();
  const rows = flattenProgramTree(tree).filter(
    ({ node }) => !search || node.title.toLowerCase().includes(search),
  );

  return (
    <>
      <Box sx={{ p: 1.5 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          type="search"
          label={label}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <List dense sx={{ maxHeight: 400, overflowY: "auto", pt: 0 }}>
        {rows.map(({ node, depth }) => (
          <ListItemButton
            key={node.id}
            selected={node.id === current?.id}
            aria-current={node.id === current?.id ? "page" : undefined}
            disabled={!node.accessible}
            onClick={() => {
              onPick();
              void navigate(programHref(node.id));
            }}
            sx={{ pl: search ? 2 : 2 + depth * 2.5 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ProgramIcon
                data={node.icon_value}
                fontSize="small"
                sx={{ color: node.theme_colour }}
              />
            </ListItemIcon>
            <ListItemText
              primary={node.title}
              secondary={
                search
                  ? programPath(tree, node.id)
                      .slice(0, -1)
                      .map(({ title }) => title)
                      .join(" › ") || undefined
                  : undefined
              }
              slotProps={{
                primary: { sx: { fontWeight: !current && depth === 0 ? 600 : undefined } },
              }}
            />
            {favourites?.includes(node.id) ? (
              <StarIcon fontSize="small" titleAccess="Favourite" sx={{ color: "warning.dark" }} />
            ) : null}
            {counts && node.accessible ? (
              <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                {counts.get(node.id) ?? 0}
              </Typography>
            ) : null}
          </ListItemButton>
        ))}
        {rows.length === 0 ? (
          <Typography variant="body2" sx={{ px: 2, py: 1, color: "text.secondary" }}>
            {empty}
          </Typography>
        ) : null}
      </List>
    </>
  );
};

/**
 * The breadcrumbs of a program, and a switcher that opens the tree of programs. A program the caller
 * cannot open shows its name only: it is text in the breadcrumbs, and grey in the tree.
 */
export const ProgramSwitcher: FC<{ tree: ProgramTree; current: ProgramNode }> = ({
  tree,
  current,
}) => {
  const [anchorRef, popover] = usePopover<HTMLButtonElement>();
  const above = programPath(tree, current.id).slice(0, -1);

  return (
    <>
      <Breadcrumbs separator={<ChevronRight fontSize="small" />} aria-label="Breadcrumb">
        <Link component={RouterLink} to="/tabbed-programs" variant="body2" underline="hover">
          Programs
        </Link>
        {above.map((node) =>
          node.accessible ? (
            <Link
              key={node.id}
              component={RouterLink}
              to={programHref(node.id)}
              variant="body2"
              underline="hover"
            >
              {node.title}
            </Link>
          ) : (
            <Typography key={node.id} variant="body2" sx={{ color: "text.secondary" }}>
              {node.title}
            </Typography>
          ),
        )}
        <Button
          ref={anchorRef}
          onClick={popover.handleToggle}
          aria-haspopup="dialog"
          aria-expanded={popover.open}
          endIcon={<UnfoldMore />}
          color="inherit"
          sx={{ fontWeight: 600 }}
          data-analytics-id="program-switcher-open"
        >
          {current.title}
        </Button>
      </Breadcrumbs>

      <ProgramTreePopover
        // oxlint-disable-next-line react/refs -- MUI anchorEl needs the DOM node; the popover only opens after the anchor has mounted
        anchorEl={anchorRef.current}
        open={popover.open}
        onClose={popover.handleClose}
        label="Switch program"
      >
        <ProgramTreeList
          tree={tree}
          current={current}
          label="Find a program"
          empty="No programs match that name."
          onPick={popover.handleClose}
        />
      </ProgramTreePopover>
    </>
  );
};
